import Step from "./Step";
import Button from "../../Components/Button";
import { ErrorMessage, Field } from 'formik'
import * as Yup from "yup"
import FormikField from '../../Components/Forms/FormikField'
import VisionPreview from "../../Components/VisionPreview";
import { visionOptions } from '../../constants/visionOptions';
import { getStepMetadata } from "../../constants/stepRegistry"
import {
    SERVICE_RESOURCE_URL, SERVICE_WORKSHEET_URL,
    VISION_RESOURCE_URL,
    VISION_WORKSHEET_URL,
} from "../../constants/resources"
import { MoreInfoVideoModal } from "../../Components/MoreInfoVideoModal"
import React from "react"

const stepMetadata = getStepMetadata("vision");

const step = {
  name: stepMetadata.name,
  title: "Create a vision for your future - with God at the center.",
  type: stepMetadata.type,
  instructions: [
    "Choose 3-5 Life Areas that feel most important right now. Then write one goal for each - something meaningful, specific and doable within a year.",
    "Take a few minutes to reflect and then share with your Guide.",
    <MoreInfoVideoModal
      src={VISION_RESOURCE_URL}
      postText={"Tips for setting goals"}
      title={"Tips for Setting Goals"}
      text={""}
      textClassName={"text-vision no-underline"}
    />,
    <MoreInfoVideoModal
      src={SERVICE_RESOURCE_URL}
      postText={"How Service strengthens Mentor Circles"}
      title={"Service Life Area"}
      text={""}
      textClassName={"text-vision no-underline"}
    />,
    <MoreInfoVideoModal
      src={VISION_WORKSHEET_URL}
      title={"Vision Worksheet"}
      postText={"Printable Vision Worksheet"}
      text={""}
      type={"pdf"}
      textClassName={"text-vision no-underline"}
      icon={"fa-regular fa-file-pen"}
    />,
      <MoreInfoVideoModal
        src={SERVICE_WORKSHEET_URL}
        title={"Vision Worksheet"}
        postText={"Printable Service Worksheet"}
        text={""}
        type={"pdf"}
        textClassName={"text-vision no-underline"}
        icon={"fa-regular fa-file-pen"}
  />,
  ],
  discussionQuestions: [
    "Which Life Areas did you choose-and why do they matter to you?",
    "What do your goals reveal about the kind of person you want to become?",
    "How could your mentors help you achieve these goals?",
  ],
  options: visionOptions,
}

  const shape = {
    'visions': Yup.array().of(Yup.string().required('Required')).min(2, 'Please select 2-5 Life Areas.').max(5, 'Please select 2-5 Life Areas.'),
    other_title: Yup.string().when('visions', {
        is: visions => visions.includes('other'),
        then: schema => schema.required('Add a title for your custom Life Area.'),
    }),
    ...visionOptions.reduce((acc, group) => {
        acc[group.value + '_vision'] = Yup.string().when('visions', {
            is: visions => visions.includes(group.value),
            then: schema => schema.required('A goal is required for each selected Life Area.'),
        })
        return acc
    }, {})
}

const schema = Yup.object().shape(shape);


function Vision() {
    const getIsComplete = (data) => !!data.visions.length;
    const getPreviewData = (data) => data.visions.map((item) => {
        const option = visionOptions.find(opt => opt.value === item);
        return {
            tag: item !== 'other' ? option.label : data.other_title,
            value: data[item + '_vision']
        }
    })
    return (
      <Step
        id="visions"
        step={step}
        Preview={VisionPreview}
        getIsComplete={getIsComplete}
        getPreviewData={getPreviewData}
        keys={[
          "visions",
          "other_title",
          "faith_vision",
          "family_vision",
          "sports_vision",
          "school_vision",
          "arts_music_vision",
          "travel_vision",
          "hobbies_vision",
          "service_vision",
          "other_vision",
          "relationships_vision",
          "work_vision",
          "justice_vision",
        ]}
        schema={schema}
        image={stepMetadata.image}
        theme={stepMetadata.theme}
        description={stepMetadata.description}
      >
        {values => (
          <>
            <FormikField
              primary={true}
              name="visions"
              instructions={step.instructions}
              title={step.title}
            >
              <div className="mb-4">
                <h3 className="mb-2 text-lg font-bold">Discussion Questions</h3>
                <p className="mb-2">
                  Use these prompts below to reflect and share with your Guide.
                </p>
                <ul className="mb-4 list-inside list-disc">
                  {step.discussionQuestions.map((question, index) => (
                    <li className={"font-bold"} key={index}>
                      {question}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="mb-4 text-sm">
                Select 4 to 5 Life Areas below and write a goal for each:
              </div>

              <div>
                {step.options.map((option, index) => {
                  return (
                    <>
                      <div key={index} className="mb-2.5">
                        <label className="flex items-center gap-4">
                          <Field
                            type="checkbox"
                            className="h-7 w-7 rounded border-gray-300 text-vision focus:ring-vision"
                            name="visions"
                            value={option.value}
                          />
                          <span>
                            {option.label}
                            {option.description ? (
                              <span className="ml-2 text-sm text-neutral-300">
                                {option.description}
                              </span>
                            ) : null}
                          </span>
                        </label>
                      </div>
                      {option.value === "other" && values.visions.includes(option.value) ? (
                        <>
                          <Field
                            name="other_title"
                            placeholder="Other title..."
                            className="mb-4 w-full rounded-md border p-2"
                          />
                          <ErrorMessage
                            name="other_title"
                            render={msg => (
                              <div className="mb-4 w-full rounded-lg bg-red-50 p-4 text-sm">
                                {msg}
                              </div>
                            )}
                          />
                        </>
                      ) : null}

                      {values.visions.includes(option.value) ? (
                        <Field
                          as="textarea"
                          name={option.value + "_vision"}
                          placeholder="Set a specific, measurable goal..."
                          className="mb-4 w-full rounded-md border p-2"
                        />
                      ) : null}
                      <ErrorMessage
                        name={option.value + "_vision"}
                        render={msg => (
                          <div className="mb-4 w-full rounded-lg bg-red-50 p-4 text-sm">{msg}</div>
                        )}
                      />
                    </>
                  )
                })}
              </div>
            </FormikField>
            <Button bgColor="bg-vision hover:bg-vision/90" type="submit">
              Save
            </Button>
          </>
        )}
      </Step>
    )
}

export default Vision;